import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useEditMode } from '../EditModeContext';
import { Image as ImageIcon, Film, X } from 'lucide-react';
import MediaPicker from './MediaPicker';

const EditableMedia = ({
  id,
  defaultSrc = '',
  alt = '',
  className = '',
  style = {},
  serviceId = null,
  mediaType = 'all', // 'image', 'video', or 'all'
  autoPlay = true,
  loop = true,
  muted = true,
  onSave = null, // Optional callback when media is changed
  children
}) => {
  const { isEditMode, updateContent, getContent } = useEditMode();
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaSrc, setMediaSrc] = useState(defaultSrc);

  // Helper to detect if URL is a video
  const isVideoUrl = (url) => {
    if (!url) return false;

    // Check for base64 video data URI
    if (url.startsWith('data:video/')) {
      return true;
    }

    // Check for video streaming services and CDNs
    const videoHosts = [
      'googlevideo.com',
      'youtube.com',
      'youtu.be',
      'vimeo.com',
      'cloudinary.com/video',
      'amazonaws.com', // AWS S3
      'cloudfront.net'
    ];

    if (videoHosts.some(host => url.toLowerCase().includes(host))) {
      return true;
    }

    // Check for mime type in URL query parameters
    if (url.includes('mime=video')) {
      return true;
    }

    // Check for video file extensions in URL (before query params)
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
    const urlWithoutParams = url.split('?')[0].split('#')[0]; // Remove query params and hash
    return videoExtensions.some(ext => urlWithoutParams.toLowerCase().endsWith(ext));
  };

  // Get the current media source (both in edit mode and normal mode)
  useEffect(() => {
    const content = getContent(id, defaultSrc, serviceId);
    // console.log('EditableMedia - Loading content:', { id, content: content?.substring(0, 50), isEditMode });
    setMediaSrc(content);
  }, [isEditMode, id, defaultSrc, serviceId, getContent]);

  // Listen for content updates
  useEffect(() => {
    const handleContentUpdate = () => {
      const content = getContent(id, defaultSrc, serviceId);
      setMediaSrc(content);
    };

    window.addEventListener('contentUpdated', handleContentUpdate);
    return () => window.removeEventListener('contentUpdated', handleContentUpdate);
  }, [id, defaultSrc, serviceId, getContent]);

  const handleClick = (e) => {
    if (isEditMode) {
      e.preventDefault();
      e.stopPropagation();
      setShowMediaPicker(true);
    }
  };

  // Render media element (image or video)
  const renderMedia = (src, isEditing = false) => {
    const isVideo = isVideoUrl(src);

    // Debug logging
    // console.log('EditableMedia - Rendering:', { id, src: src?.substring(0, 50), isVideo });

    if (isVideo) {
      return (
        <video
          src={src || defaultSrc}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          playsInline
          className={className}
          style={style}
          onError={(e) => {
            console.error('Video load error for:', id, e);
          }}
          onLoadedData={() => {
            // console.log('Video loaded successfully:', id);
          }}
        >
          <source src={src || defaultSrc} type="video/mp4" />
        </video>
      );
    } else {
      return (
        <img
          src={src || defaultSrc}
          alt={alt}
          className={className}
          style={style}
          onError={(e) => {
            console.error('Image load error for:', id);
            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f3f4f6" width="400" height="300"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="18" dy="10.5" font-weight="400" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
          }}
          onLoad={() => {
            // console.log('Image loaded successfully:', id);
          }}
        />
      );
    }
  };

  // In edit mode - show with edit overlay
  if (isEditMode) {
    const isVideo = isVideoUrl(mediaSrc);

    // Check if className includes full sizing - if so, make wrapper fill container
    const isFullSize = className.includes('w-full') && className.includes('h-full');
    const wrapperClass = isFullSize
      ? "relative group block w-full h-full cursor-pointer"
      : "relative group inline-block cursor-pointer";

    return (
      <>
        <div className={wrapperClass} onClick={handleClick}>
          {renderMedia(mediaSrc, true)}
          <div className="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center pointer-events-none">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-lg px-4 py-2 shadow-lg">
              <div className="flex items-center gap-2 text-blue-600">
                {isVideo ? <Film size={20} /> : <ImageIcon size={20} />}
                <span className="text-sm font-medium">
                  Click to change {isVideo ? 'video' : 'image'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* MediaPicker rendered via Portal to avoid container constraints */}
        {showMediaPicker && ReactDOM.createPortal(
          <MediaPicker
            isOpen={showMediaPicker}
            onClose={() => setShowMediaPicker(false)}
            onSelect={(url) => {
              // console.log('✅ Media selected from library:', url);
              setMediaSrc(url);
              updateContent(id, url, serviceId);
              setShowMediaPicker(false);
              // Call onSave callback if provided (for D1 sync)
              if (onSave) {
                onSave(url);
              }
              // updateContent already dispatches contentUpdated event with key detail
            }}
            currentUrl={mediaSrc}
            mediaType={mediaType}
          />,
          document.body
        )}
      </>
    );
  }

  // Not in edit mode - render normally
  if (children) {
    return children;
  }

  return renderMedia(mediaSrc);
};

export default EditableMedia;
